import React from "react";
import { Dialog } from "@vitalsource/vstui";
import styled from "styled-components";
import { green } from "@vitalsource/vstui/colors";
import { createThemedComponent } from "@vitalsource/vstui/themes";
import Lightbulb from "./lightbulb";
import { debounce } from "throttle-debounce";
import FigureBlock from "./figure-block";
import { findNearestScrollParent } from "../../../utils/dom-utils";
import BookStore from "../../../stores/book-store";
import { getFigureBlocksWithTocInfo, getFiguresByChapter } from "./selectors";

const CustomDialog = createThemedComponent(Dialog, {
  Dialog_width_large: "700px"
});

const SIDE_PADDING = 12;

const Styled = {
  Content: styled.div`
    margin: -20px -24px 0;
  `,
  Banner: styled.div`
    background-color: ${green.getShade(1)};
    padding: ${SIDE_PADDING}px 47px 12px 24px;
    margin: 0;
    min-height: 56px;
    display: flex;
    align-items: center;
    justify-content: space-between;
  `,
  BulbIcon: styled(Lightbulb)`
    color: ${green.getShade(6)};
    margin-right: 20px;
  `,
  BannerText: styled.p`
    margin: 0;
    line-height: 17px;
    flex: 1;
  `,
  BlockContainer: styled.div`
    padding: ${SIDE_PADDING}px 24px 0;
  `
};

export default class FigurePicker extends React.Component {
  state = {
    blocksEverInView: {},
    selectedFigure: null
  };

  blockRefs = [];
  blockSizes = [];

  componentDidMount() {
    this.scroller = findNearestScrollParent(this.content);
    this.scroller.addEventListener("scroll", this.handleScroll);
    window.addEventListener("resize", this.invalidateSizes);
    if (this.props.isOpen) {
      this.onOpen();
    }
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    if (this.props.isOpen && !prevProps.isOpen) {
      this.onOpen();
    }
  }

  componentWillUnmount() {
    this.scroller.removeEventListener("scroll", this.handleScroll);
    window.removeEventListener("resize", this.invalidateSizes);
    clearTimeout(this.timeoutId);
  }

  handleScroll = debounce(75, () => {
    this.updateImages();
  });

  invalidateSizes = debounce(150, () => {
    this.blockSizes = [];
    this.updateImages();
  });

  onOpen() {
    this.timeoutId = window.setTimeout(() => {
      this.scrollToCurrentLocation();
      this.updateImages()
    }, 200);
  }

  onSelectFigure = id => {
    this.setState({
      selectedFigure: id
    });
  };

  scrollToCurrentLocation() {
    const book = BookStore.get(this.props.isbn);
    const toc = book.getTOC();
    if (book.lastCFI && book.pageCFI && toc && toc.length > 0) {
      const fullCfi = Fraction.Helpers.stripAssertions(book.pageCFI + "!" + Fraction.Helpers.stripScrollPosition(book.lastCFI));
      const blocks = getFigureBlocksWithTocInfo({
        figures: this.props.figures,
        toc
      });
      const index = Fraction.Helpers.findIndexMatchingCfiFallBackToBefore(blocks, item => item.tocCfi, fullCfi);
      if (index >= 0) {
        this.blockRefs[index].scrollIntoView();
      }
    } else {
      this.blockRefs[0].scrollIntoView();
    }
  }

  updateImages() {
    const blocksEverInView = { ...this.state.blocksEverInView };
    // Only compute once for computational efficiency
    const windowHeight = window.innerHeight;
    const scrollTop = this.scroller.scrollTop;
    for (let i = 0; i < this.blockRefs.length; i++) {
      const block = this.blockRefs[i];
      if (!blocksEverInView[i]) {
        let size = this.blockSizes[i];
        // Cache sizes to reduce computation load
        if (!this.blockSizes[i]) {
          size = {
            top: block.offsetTop,
            height: block.offsetHeight
          };
          this.blockSizes[i] = size;
        }
        const visible =
          size.top + size.height >= scrollTop &&
          size.top <= windowHeight + scrollTop;
        if (visible) {
          blocksEverInView[i] = true;
        }
      }
    }
    this.setState({
      blocksEverInView: blocksEverInView
    });
  }

  addToCard = () => {
    if (this.state.selectedFigure) {
      this.props.onAdd(this.state.selectedFigure);
    }
  };

  render() {
    if (!this.props.figures) {
      return null;
    }

    return (
      <CustomDialog
        open={this.props.isOpen}
        onRequestClose={this.props.onRequestClose}
        size="large"
        title={i18n.t("flashcards.images.select-figure")}
        darkHeader
        primaryActionButton={{
          label: i18n.t("flashcards.images.add-to-card"),
          onClick: this.addToCard
        }}
        secondaryActionButton={{
          label: i18n.t("base.cancel"),
          onClick: this.props.onRequestClose
        }}
        rootSelector="#bookshelf"
      >
        <Styled.Content innerRef={el => (this.content = el)}>
          <Styled.Banner>
            <Styled.BulbIcon width={19.5} height={30} />
            <Styled.BannerText>
              {i18n.t("flashcards.images.banner-tip")}
            </Styled.BannerText>
          </Styled.Banner>
          {getFiguresByChapter(this.props.figures).map((chapter, i) => (
            <Styled.BlockContainer key={i}>
              <FigureBlock
                title={chapter.title}
                figures={chapter.figures}
                renderImages={this.state.blocksEverInView[i] || false}
                selectedFigure={this.state.selectedFigure}
                onSelectFigure={this.onSelectFigure}
                innerRef={el => (this.blockRefs[i] = el)}
              />
            </Styled.BlockContainer>
          ))}
        </Styled.Content>
      </CustomDialog>
    );
  }
}
